/* ==========================================================================
   Intake Detail Page
   Task F36: Full intake detail with status timeline and action buttons
   ========================================================================== */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  ExternalLink,
  Download,
  CheckCircle2,
  Circle,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import {
  getIntakeDetail,
  createAdminSession,
  downloadCsvTemplate,
  importIntakeCatalogCsv,
  type IntakeDetail as IntakeDetailType,
  type IntakeStatus,
} from '@/api/internal'
import { ApiClientError } from '@/api/client'
import { ProvisionModal } from '@/components/internal/ProvisionModal'
import { GoLiveModal } from '@/components/internal/GoLiveModal'
import { CompleteModal } from '@/components/internal/CompleteModal'
import { CsvCatalogUpload } from '@/components/catalog/CsvCatalogUpload'

/* --------------------------------------------------------------------------
   Status Badge
   -------------------------------------------------------------------------- */

const STATUS_STYLES: Record<IntakeStatus, string> = {
  submitted: 'bg-amber-100 text-amber-800',
  in_review: 'bg-blue-100 text-blue-800',
  provisioned: 'bg-purple-100 text-purple-800',
  live: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
}

const STATUS_LABELS: Record<IntakeStatus, string> = {
  submitted: 'Submitted',
  in_review: 'In Review',
  provisioned: 'Provisioned',
  live: 'Live',
  completed: 'Completed',
}

function StatusBadge({ status }: { status: IntakeStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-800'}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  )
}

/* --------------------------------------------------------------------------
   Status Timeline
   -------------------------------------------------------------------------- */

const TIMELINE_STEPS: IntakeStatus[] = [
  'submitted',
  'provisioned',
  'live',
  'completed',
]

function StatusTimeline({ currentStatus }: { currentStatus: IntakeStatus }) {
  const currentIndex = TIMELINE_STEPS.indexOf(currentStatus)

  return (
    <div className="flex items-center gap-0 w-full">
      {TIMELINE_STEPS.map((step, i) => {
        const isComplete = i <= currentIndex
        const isCurrent = i === currentIndex

        return (
          <div key={step} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              {isComplete ? (
                <CheckCircle2
                  className={`h-6 w-6 ${
                    isCurrent ? 'text-deep-purple' : 'text-green-500'
                  }`}
                />
              ) : (
                <Circle className="h-6 w-6 text-gray-300" />
              )}
              <span
                className={`text-xs mt-1 ${
                  isComplete ? 'text-charcoal font-medium' : 'text-charcoal/40'
                }`}
              >
                {STATUS_LABELS[step]}
              </span>
            </div>
            {i < TIMELINE_STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 ${
                  i < currentIndex ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Color Swatch
   -------------------------------------------------------------------------- */

function ColorSwatch({ color, label }: { color: string | null; label: string }) {
  if (!color) return null
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-6 h-6 rounded border border-gray-200"
        style={{ backgroundColor: color }}
      />
      <span className="text-sm text-charcoal/70">
        {label}: {color}
      </span>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function IntakeDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [intake, setIntake] = useState<IntakeDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [sessionLoading, setSessionLoading] = useState(false)

  // Modal states
  const [showProvisionModal, setShowProvisionModal] = useState(false)
  const [showGoLiveModal, setShowGoLiveModal] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)

  const fetchDetail = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(undefined)

    try {
      const data = await getIntakeDetail(id)
      setIntake(data)
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          navigate('/internal/login', { replace: true })
          return
        }
        setError(err.getUserMessage())
      } else {
        setError('Failed to load intake details')
      }
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  const handleOpenAdminPortal = async () => {
    if (!intake) return
    setSessionLoading(true)

    try {
      const session = await createAdminSession(intake.id)
      // Open admin portal in a new tab with session token
      const url = `${session.admin_portal_url}?session=${session.token}`
      window.open(url, '_blank')
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('Failed to create admin session')
      }
    } finally {
      setSessionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-lavender border-t-deep-purple" />
          <p className="text-sm text-charcoal/70">Loading intake details...</p>
        </div>
      </div>
    )
  }

  if (error && !intake) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Alert variant="error">{error}</Alert>
        <Link
          to="/internal/intake"
          className="inline-flex items-center gap-1 mt-4 text-sm text-deep-purple hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Link>
      </div>
    )
  }

  if (!intake) return null

  const status = intake.status

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl">
      {/* Back link */}
      <Link
        to="/internal/intake"
        className="inline-flex items-center gap-1 text-sm text-deep-purple hover:underline mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to list
      </Link>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-2xl text-charcoal">
            {intake.pharmacy_name}
          </h1>
          <p className="text-sm text-charcoal/60 mt-1">{intake.admin_email}</p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Status timeline */}
      <Card className="p-4 sm:p-6 mb-6">
        <StatusTimeline currentStatus={status} />
      </Card>

      {/* Action buttons */}
      <Card className="p-4 sm:p-6 mb-6">
        <div className="flex flex-wrap gap-3">
          {status === 'submitted' && (
            <Button
              variant="primary"
              onClick={() => setShowProvisionModal(true)}
            >
              Provision Account
            </Button>
          )}

          {(status === 'provisioned' || status === 'live' || status === 'completed') && (
            <Button
              variant="secondary"
              onClick={handleOpenAdminPortal}
              loading={sessionLoading}
              disabled={sessionLoading}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Admin Portal
            </Button>
          )}

          {status === 'provisioned' && (
            <Button
              variant="primary"
              onClick={() => setShowGoLiveModal(true)}
            >
              Mark Go-Live Ready
            </Button>
          )}

          {status === 'live' && (
            <Button
              variant="primary"
              onClick={() => setShowCompleteModal(true)}
            >
              Complete & Invite Admin
            </Button>
          )}

          {status === 'completed' && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">Onboarding Complete</span>
            </div>
          )}
        </div>
      </Card>

      {/* Detail cards */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Pharmacy Info */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-charcoal mb-3">
            Pharmacy Info
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Name</dt>
              <dd className="text-charcoal">{intake.pharmacy_name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Admin Email</dt>
              <dd className="text-charcoal">{intake.admin_email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Fax Number</dt>
              <dd className="text-charcoal">{intake.fax_number || '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Preferred Slug</dt>
              <dd className="text-charcoal font-mono text-xs">
                {intake.preferred_slug}
              </dd>
            </div>
            {intake.notes && (
              <div>
                <dt className="text-charcoal/60 mb-1">Notes</dt>
                <dd className="text-charcoal bg-gray-50 rounded p-2 text-xs">
                  {intake.notes}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Subscription Info */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-charcoal mb-3">
            Subscription
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Tier</dt>
              <dd className="text-charcoal capitalize">{intake.tier || '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Billing Cycle</dt>
              <dd className="text-charcoal capitalize">
                {intake.billing_cycle || '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-charcoal/60">Submitted</dt>
              <dd className="text-charcoal">
                {new Date(intake.submitted_at).toLocaleString()}
              </dd>
            </div>
            {intake.stripe_customer_id && (
              <div className="flex justify-between">
                <dt className="text-charcoal/60">Stripe Customer</dt>
                <dd className="text-charcoal font-mono text-xs">
                  {intake.stripe_customer_id}
                </dd>
              </div>
            )}
          </dl>
        </Card>

        {/* Branding */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-charcoal mb-3">Branding</h3>
          <div className="space-y-3">
            {intake.logo_file_url ? (
              <div>
                <p className="text-xs text-charcoal/60 mb-2">Logo</p>
                <img
                  src={intake.logo_file_url}
                  alt="Pharmacy logo"
                  className="h-16 w-auto rounded border border-gray-200 object-contain bg-white p-1"
                />
              </div>
            ) : (
              <p className="text-sm text-charcoal/40">No logo uploaded</p>
            )}
            <div className="flex gap-4">
              <ColorSwatch color={intake.primary_color} label="Primary" />
              <ColorSwatch color={intake.secondary_color} label="Secondary" />
            </div>
            {!intake.primary_color && !intake.secondary_color && (
              <p className="text-sm text-charcoal/40">No colors specified</p>
            )}
          </div>
        </Card>

        {/* Formulary & Portal */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-sm font-semibold text-charcoal mb-3">
            Formulary & Portal
          </h3>
          <div className="space-y-3">
            {intake.formulary_file_url ? (
              <div>
                <p className="text-xs text-charcoal/60 mb-1">Formulary File</p>
                <a
                  href={intake.formulary_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-deep-purple hover:underline"
                >
                  <Download className="h-4 w-4" />
                  {intake.formulary_file_name || 'Download'}
                </a>
              </div>
            ) : (
              <p className="text-sm text-charcoal/40">No formulary uploaded</p>
            )}

            {intake.pharmacy_slug && (
              <div>
                <p className="text-xs text-charcoal/60 mb-1">Portal URL</p>
                <Link
                  to={`/rx/${intake.pharmacy_slug}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-sm text-deep-purple hover:underline font-mono"
                >
                  /rx/{intake.pharmacy_slug}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        </Card>

        {/* CSV Catalog Import — shown only for provisioned+ intakes */}
        {intake.pharmacy_slug && (
          <Card className="p-4 sm:p-6">
            <h3 className="text-sm font-semibold text-charcoal mb-3">
              Import Catalog (CSV)
            </h3>
            <p className="text-xs text-charcoal/60 mb-3">
              Bulk import products into this pharmacy&apos;s catalog from a CSV file.
              Download the template, fill it in, and upload.
            </p>
            <CsvCatalogUpload
              onImport={(file) => importIntakeCatalogCsv(intake.id, file)}
              onDownloadTemplate={downloadCsvTemplate}
            />
          </Card>
        )}
      </div>

      {/* Modals */}
      <ProvisionModal
        open={showProvisionModal}
        onClose={() => setShowProvisionModal(false)}
        intake={intake}
        onSuccess={fetchDetail}
      />
      <GoLiveModal
        open={showGoLiveModal}
        onClose={() => setShowGoLiveModal(false)}
        intake={intake}
        onSuccess={fetchDetail}
      />
      <CompleteModal
        open={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        intake={intake}
        onSuccess={fetchDetail}
      />
    </div>
  )
}

export default IntakeDetail
